/**
 * Lightweight telemetry composable for Google Analytics events.
 */

import { trackSentryPerformanceMetric, trackSentryTelemetryEvent } from './sentryMetrics'

type TelemetryParamValue = string | number | boolean
type TelemetryProperties = Record<string, unknown>
type TelemetryParams = Record<string, TelemetryParamValue>

interface TelemetryEvent {
  event: string
  properties: TelemetryParams
}

const DEFAULT_PAGE_TITLE = 'Kaizen Platform'
export const ANALYTICS_HOSTNAME = 'kaizen.msgsndr.net'

const isTelemetryParamValue = (value: unknown): value is TelemetryParamValue => {
  if (typeof value === 'number') return Number.isFinite(value)
  return typeof value === 'string' || typeof value === 'boolean'
}

const sanitizeTelemetryParams = (properties: TelemetryProperties = {}): TelemetryParams => {
  return Object.entries(properties).reduce<TelemetryParams>((params, [key, value]) => {
    if (isTelemetryParamValue(value)) {
      return {
        ...params,
        [key]: value,
      }
    }
    return params
  }, {})
}

const getPageLocation = (page: string): string => {
  if (/^https?:\/\//.test(page)) return page
  if (typeof window === 'undefined') return page
  return `${window.location.origin}${page.startsWith('/') ? page : `/${page}`}`
}

const getPageTitle = (): string => {
  if (typeof document === 'undefined') return DEFAULT_PAGE_TITLE
  return document.title || DEFAULT_PAGE_TITLE
}

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message
  return String(error)
}

const getErrorStack = (error: unknown): string | undefined => {
  return error instanceof Error ? error.stack : undefined
}

const getCurrentHostname = (): string => {
  if (typeof window === 'undefined') return ''
  return window.location.hostname
}

class TelemetryService {
  events: TelemetryEvent[]
  startTime: number
  getHostname: () => string

  constructor() {
    this.events = []
    this.startTime = Date.now()
    this.getHostname = getCurrentHostname
  }

  track(event: string, properties: TelemetryProperties = {}) {
    if (!event.trim()) return

    const sanitizedProperties = sanitizeTelemetryParams(properties)
    this.events.push({
      event,
      properties: sanitizedProperties,
    })
    trackSentryTelemetryEvent(event, sanitizedProperties)
    this.sendEvent(event, sanitizedProperties)
  }

  trackPageView(page: string, properties: TelemetryProperties = {}) {
    const sanitizedProperties = sanitizeTelemetryParams(properties)
    const {
      page_location: providedPageLocation,
      page_path: _providedPagePath,
      page_title: providedPageTitle,
      ...customProperties
    } = sanitizedProperties

    this.track('page_view', {
      page_path: page,
      page_location: typeof providedPageLocation === 'string' ? providedPageLocation : getPageLocation(page),
      page_title: typeof providedPageTitle === 'string' ? providedPageTitle : getPageTitle(),
      ...customProperties,
    })
  }

  trackUserAction(action: string, properties: TelemetryProperties = {}) {
    this.track('user_action', {
      action,
      ...properties,
    })
  }

  trackError(error: unknown, properties: TelemetryProperties = {}) {
    this.track('error', {
      error: getErrorMessage(error),
      stack: getErrorStack(error),
      ...properties,
    })
  }

  trackException(description: string, properties: TelemetryProperties = {}) {
    this.track('exception', {
      description,
      fatal: false,
      ...properties,
    })
  }

  trackPerformance(metric: string, value: number, properties: TelemetryProperties = {}) {
    trackSentryPerformanceMetric(metric, value, properties)
    this.track('performance', {
      metric,
      value,
      ...properties,
    })
  }

  trackFeatureUsage(feature: string, properties: TelemetryProperties = {}) {
    this.track('feature_usage', {
      feature,
      ...properties,
    })
  }

  sendEvent(event: string, properties: TelemetryParams) {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return
    if (this.getHostname() !== ANALYTICS_HOSTNAME) return
    window.gtag('event', event, properties)
  }

  getSessionDuration() {
    return Date.now() - this.startTime
  }

  getEvents() {
    return this.events.map(telemetryEvent => ({
      event: telemetryEvent.event,
      properties: { ...telemetryEvent.properties },
    }))
  }

  clearEvents() {
    this.events = []
  }
}

// Global telemetry instance
const telemetry = new TelemetryService()

export function useTelemetry() {
  const track = (event: string, properties: TelemetryProperties = {}) => {
    telemetry.track(event, properties)
  }

  const trackPageView = (page: string, properties: TelemetryProperties = {}) => {
    telemetry.trackPageView(page, properties)
  }

  const trackUserAction = (action: string, properties: TelemetryProperties = {}) => {
    telemetry.trackUserAction(action, properties)
  }

  const trackError = (error: unknown, properties: TelemetryProperties = {}) => {
    telemetry.trackError(error, properties)
  }

  const trackException = (description: string, properties: TelemetryProperties = {}) => {
    telemetry.trackException(description, properties)
  }

  const trackPerformance = (metric: string, value: number, properties: TelemetryProperties = {}) => {
    telemetry.trackPerformance(metric, value, properties)
  }

  const trackFeatureUsage = (feature: string, properties: TelemetryProperties = {}) => {
    telemetry.trackFeatureUsage(feature, properties)
  }

  const getSessionDuration = () => {
    return telemetry.getSessionDuration()
  }

  const getEvents = () => {
    return telemetry.getEvents()
  }

  const clearEvents = () => {
    telemetry.clearEvents()
  }

  return {
    track,
    trackPageView,
    trackUserAction,
    trackError,
    trackException,
    trackPerformance,
    trackFeatureUsage,
    getSessionDuration,
    getEvents,
    clearEvents,
  }
}

/**
 * Telemetry composable for specific features
 */
export function useFeatureTelemetry(featureName: string) {
  const { trackFeatureUsage, trackError } = useTelemetry()

  const trackFeatureStart = (action: string, properties: TelemetryProperties = {}) => {
    trackFeatureUsage(`${featureName}_start`, {
      action,
      ...properties,
    })
  }

  const trackFeatureEnd = (action: string, properties: TelemetryProperties = {}) => {
    trackFeatureUsage(`${featureName}_end`, {
      action,
      ...properties,
    })
  }

  const trackFeatureError = (action: string, error: unknown, properties: TelemetryProperties = {}) => {
    trackError(error, {
      feature: featureName,
      action,
      ...properties,
    })
  }

  return {
    trackFeatureStart,
    trackFeatureEnd,
    trackFeatureError,
  }
}

/**
 * Performance monitoring composable
 */
export function usePerformanceMonitoring() {
  const { trackPerformance } = useTelemetry()

  const measurePerformance = <Args extends unknown[], Result>(name: string, fn: (...args: Args) => Promise<Result> | Result) => {
    return async (...args: Args): Promise<Awaited<Result>> => {
      const start = performance.now()
      try {
        const result = await fn(...args)
        const duration = performance.now() - start
        trackPerformance(name, duration, { success: true })
        return result as Awaited<Result>
      } catch (error) {
        const duration = performance.now() - start
        trackPerformance(name, duration, { success: false, error: getErrorMessage(error) })
        throw error
      }
    }
  }

  const measureComponentRender = (componentName: string) => {
    const start = performance.now()

    return () => {
      const duration = performance.now() - start
      trackPerformance(`${componentName}_render`, duration)
    }
  }

  return {
    measurePerformance,
    measureComponentRender,
  }
}

// Export the global telemetry instance for direct access
export { telemetry }
